import React, { useState, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { incidentsApi } from '../../../../functions/IncidentsApi';
import { debounce } from 'lodash';
import OutlinedInput from '@mui/material/OutlinedInput';

const ApiAutocomplete = ({
  id,
  value,
  onChange,
  enumClass,
  multiple = false,
  disabled = false,
  size = 'small',
  placeholder = 'Select option...',
  sx = {},
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchOptions = useCallback(
    debounce(async (searchValue = '') => {
      if (!enumClass) {
        return;
      }

      setLoading(true);
      try {
        const response = await incidentsApi.getAutocompleteOptions(
          enumClass,
          searchValue
        );

        if (response.status === 200) {
          setOptions(response.data.data || []);
        } else throw response;
      } catch (error) {
        console.error('Error fetching options:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [enumClass]
  );

  // init selector options
  useEffect(() => {
    fetchOptions(value ?? '');
  }, []);

  return (
    <Autocomplete
      id={id}
      multiple={multiple}
      options={options}
      value={multiple ? value || [] : value || null}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        // only users change allow
        if (!event) {
          return;
        }
        fetchOptions(newInputValue);
      }}
      loading={loading}
      disabled={disabled}
      size={size}
      sx={sx}
      getOptionLabel={(option) => {
        if (typeof option === 'number') {
          return option.toString();
        }

        return option ? option.replace(/_/g, ' ') : '';
      }}
      isOptionEqualToValue={(option, value) => {
        return option === value;
      }}
      renderInput={(params) => {
        return (
          <OutlinedInput
            {...params.InputProps}
            inputProps={params.inputProps}
            placeholder={placeholder}
            size={size}
            fullWidth
            variant="outlined"
            endAdornment={
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            }
          />
        );
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ padding: '8px 16px !important' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {option.replace(/_/g, ' ')}
          </Typography>
        </Box>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            key={option}
            label={option.replace(/_/g, ' ')}
            size="small"
            {...getTagProps({ index })}
          />
        ))
      }
    />
  );
};

export default ApiAutocomplete;
